import { CancelledError } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

const MAX_ATTEMPTS = 3;

interface FetchQueryOptions<T> {
  queryKey: readonly unknown[];
  queryFn: () => Promise<T>;
  staleTime?: number;
}

/**
 * fetchQuery that survives a mid-flight cache purge.
 *
 * The login identity boundary (useAuthIdentitySync -> purgeAuthState ->
 * queryClient.clear()) lands a frame AFTER the post-login navigation starts,
 * silently cancelling every in-flight loader query with a CancelledError.
 * A cancellation means the fetch was superseded, not that the data failed:
 * the purge settles the cache synchronously before the rejection propagates,
 * so an immediate re-fetch resolves cleanly instead of rendering a terminal
 * section error on every fresh login. Other failures propagate unchanged.
 */
export async function purgeResilientFetchQuery<T>(
  queryClient: QueryClient,
  options: FetchQueryOptions<T>
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await queryClient.fetchQuery(options);
    } catch (error) {
      if (!(error instanceof CancelledError) || attempt === MAX_ATTEMPTS - 1) {
        throw error;
      }
    }
  }
}
